import { hasPoBoxAddress } from '@domgen/shared/utils/address/hasPoBoxAddress';
import { z } from 'zod';

// Validators are curried: call with optional message (or params) to get (value) => string
export const formValidators = {
  required:
    (message: string) =>
    (value: unknown): string => {
      const v = value ?? '';
      return !String(v).trim() ? message : '';
    },

  // Wrap validators for an optional field: skip them entirely when no value is
  // present, but apply them as normal once the user has entered something.
  optional:
    (validators: Array<(value: unknown) => string>) =>
    (value: unknown): string => {
      if (!String(value ?? '').trim()) return '';
      for (const validator of validators) {
        const error = validator(value);
        if (error) return error;
      }
      return '';
    },
  alpha:
    (message: string) =>
    (value: unknown): string => {
      const v = value ?? '';
      return /^[A-Za-zÀ-Ÿà-ÿ\s'-]+$/.test(String(v)) ? '' : message;
    },
  alphanumeric:
    (message: string) =>
    (value: unknown): string => {
      const v = value ?? '';
      return /^[A-Za-z0-9]*$/.test(String(v)) ? '' : message;
    },
  // Allow spaces (e.g. model names like "C2 Pro" / "C2 Ultra")
  alphanumericWithSymbols:
    (message: string) =>
    (value: unknown): string => {
      const v = value ?? '';
      return /^[A-Za-z0-9+\- ]*$/.test(String(v)) ? '' : message;
    },
  /** Pricing */
  numeric:
    (message: string) =>
    (value: unknown): string => {
      const v = value ?? '';
      return /^[0-9]+(\.[0-9]{1,2})?$/.test(String(v)) ? '' : message;
    },
  email:
    (message: string) =>
    (value: unknown): string => {
      const v = value ?? '';
      return z.email().safeParse(String(v)).success ? '' : message;
    },
  usPhoneNumber:
    (message: string) =>
    (value: unknown): string => {
      const raw = String(value ?? '');
      if (!/^\+1[0-9]{10}$/.test(raw)) {
        return message;
      }
      return '';
    },
  minLength:
    (min: number, message: string) =>
    (value: unknown): string => {
      const v = String(value ?? '');
      return v.length >= min ? '' : message;
    },
  maxLength:
    (max: number, message: string) =>
    (value: unknown): string => {
      const v = String(value ?? '');
      return v.length <= max ? '' : message;
    },
  maxNumber:
    (max: number, message: string) =>
    (value: unknown): string => {
      const v = Number(value);
      return !isNaN(v) && v <= max ? '' : message;
    },
  minNumber:
    (min: number, message: string) =>
    (value: unknown): string => {
      const v = Number(value);
      return !isNaN(v) && v >= min ? '' : message;
    },
  noDigits:
    (message: string) =>
    (value: unknown): string => {
      const v = String(value);
      return /\d/.test(v) ? message : '';
    },
  usZipCode:
    (message: string) =>
    (value: unknown): string => {
      const v = String(value);
      return /^\d{5}(-\d{4})?$/.test(v) ? '' : message;
    },
  hasPoBox:
    (message: string) =>
    (value: unknown): string => {
      const v = String(value);
      return hasPoBoxAddress(v) ? message : '';
    },
};
